import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Progress, Button, Badge,
  Alert, AlertIcon, Spinner, Flex, Divider, useToast,
} from '@chakra-ui/react';
import UserSettingsLayout from './UserSettingsLayout';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useSearchParams } from 'react-router-dom';
import CreditTopUpModal from './CreditTopUpModal';

// Fallback only — the real, admin-configured costs come from credits.costs
// (planStatus.credits.costs, sourced live from the credit_costs table). These
// existed as hardcoded values before and had drifted out of sync with the
// actual configured pricing; keep them only as a safety net if that API data
// is ever unavailable.
const DEFAULT_CREDIT_COSTS = {
  'strategy-section': 2,
  'competitor-analysis': 6,
  'document': 9,
  'action-plan': 6,
  'pulse': 2,
  'agent-task': 1,
  'mcp-call': 1,
};

const ACTION_LABELS = {
  'strategy-section': 'Product Strategy',
  'competitor-analysis': 'Competitor Analysis',
  'document': 'Documents',
  'action-plan': 'Action Plans',
  'pulse': 'Pulse',
  'agent-task': 'Jobs & missions',
  'mcp-call': 'MCP calls',
};

const FEATURE_COLORS = {
  'strategy-section':   '#2d3748',  // brand.700
  'competitor-analysis':'#D4674A',  // card.coral
  'document':           '#3DAA6E',  // card.green
  'action-plan':        '#718096',  // brand.500
  'pulse':              '#a0aec0',  // brand.400
  'agent-task':         '#805AD5',  // purple.500
  'mcp-call':           '#3182CE',  // blue.500
  'other':              '#cbd5e0',  // brand.300
};

// iPhone-Storage-style stacked bar
const StackedCreditBar = ({ segments, total, height = '12px', showRemaining = true, remaining = 0 }) => {
  if (total <= 0) return (
    <Box h={height} bg="gray.100" borderRadius="full" w="100%" />
  );
  return (
    <Flex h={height} borderRadius="full" overflow="hidden" w="100%" bg="gray.100">
      {segments.map(({ key, value }) => {
        if (!value) return null;
        const pct = Math.min(100, (value / total) * 100);
        return (
          <Box
            key={key}
            w={`${pct}%`}
            bg={FEATURE_COLORS[key] || FEATURE_COLORS.other}
            flexShrink={0}
          />
        );
      })}
      {showRemaining && remaining > 0 && (
        <Box flex={1} bg="gray.100" />
      )}
    </Flex>
  );
};

const PlanUsage = () => {
  const [planStatus, setPlanStatus] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [topUpInitialMode, setTopUpInitialMode] = useState('topup');

  const openTopUp = (mode = 'topup') => {
    setTopUpInitialMode(mode);
    setIsTopUpOpen(true);
  };
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetchWithAuth('/api/subscription/status'),
        fetchWithAuth('/api/credits/history'),
      ]);
      if (statusRes.ok) {
        const data = await statusRes.json();
        setPlanStatus(data);
      }
      if (historyRes.ok) setCreditHistory(await historyRes.json());
    } catch (err) {
      console.error('Error fetching plan status:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    if (searchParams.get('topup') === 'success') {
      toast({ title: 'Top-up successful', description: 'Your credits have been added.', status: 'success', duration: 4000, isClosable: true });
    }
  }, [fetchStatus, searchParams, toast]);

  const limits = planStatus?.limits || {};
  const usage = planStatus?.usage || {};
  const credits = planStatus?.credits || {};
  const plansList = usage.plansList || [];
  const isPro = planStatus?.planType === 'pro';
  const plansUsed = usage.plansCount || 0;

  // agent_task (underscore) is a legacy/free duplicate of agent-task in
  // credit_costs — normalize to the hyphenated key this component uses.
  const CREDIT_COSTS = {
    ...DEFAULT_CREDIT_COSTS,
    ...Object.fromEntries(
      Object.entries(credits.costs || {}).map(([key, value]) => [
        key === 'agent_task' ? 'agent-task' : key,
        value.credits,
      ])
    ),
  };

  const chatsUsed = usage.dailyChatCount || 0;
  const chatsMax = limits.maxDailyChats ?? 25;
  const isNearChatLimit = chatsMax !== null && (chatsMax - chatsUsed) <= 5;

  const creditTotal = credits.total ?? 0;
  const creditAllowance = credits.allowance ?? (isPro ? 300 : 30);
  const monthlyBalance = credits.monthlyBalance ?? 0;
  const purchasedBalance = credits.purchasedBalance ?? 0;
  const creditsSpent = Math.max(0, creditAllowance - monthlyBalance);
  const isExhaustedCredits = creditTotal === 0;
  const isLowCredits = creditTotal <= creditAllowance * 0.10 && !isExhaustedCredits;
  const isWarningCredits = creditTotal <= creditAllowance * 0.20 && !isLowCredits && !isExhaustedCredits;

  // Spend breakdown from credit history
  const spendByFeature = creditHistory
    .filter(t => t.type === 'deduction')
    .reduce((acc, t) => {
      const key = t.action_type || 'other';
      acc[key] = (acc[key] || 0) + Math.abs(t.amount);
      return acc;
    }, {});
  const totalSpent = Object.values(spendByFeature).reduce((s, v) => s + v, 0);

  // Ordered segments for stacked bar (largest first for visual clarity)
  const spendSegments = Object.entries(spendByFeature)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({ key, value }));

  const formatResetDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <UserSettingsLayout>
        <Flex align="center" justify="center" minH="300px">
          <Spinner size="lg" color="brand.600" thickness="3px" />
        </Flex>
      </UserSettingsLayout>
    );
  }

  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8}>

        {/* Page header */}
        <HStack mb={8} justify="space-between" align="flex-start" maxW="720px">
          <Box>
            <Text fontSize="xl" fontWeight="700" color="brand.700">Usage</Text>
            <Text fontSize="sm" color="gray.500" mt={1}>Your credits and how your AI employees spent them.</Text>
          </Box>
          <Badge px={3} py={1} borderRadius="full"
            colorScheme={isPro ? 'brand' : 'gray'}
            textTransform="capitalize" fontSize="sm" flexShrink={0}>
            {planStatus?.planType || 'free'} Plan
          </Badge>
        </HStack>

        <VStack align="stretch" width="100%" maxW="720px" spacing={6}>

          {/* Low-credit alert */}
          {(isExhaustedCredits || isLowCredits || isWarningCredits) && (
            <Alert status={isExhaustedCredits || isLowCredits ? "error" : "warning"} borderRadius="lg">
              <AlertIcon />
              <Box flex="1">
                <Text fontWeight="medium">
                  {isExhaustedCredits ? "You've run out of credits" : isLowCredits ? "Almost out of credits" : "Running low on credits"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {isExhaustedCredits
                    ? isPro ? "Top up to get your AI employees back to work." : "Top up or upgrade to Pro to continue."
                    : isPro ? "Top up to keep your AI employees working." : "Top up, or upgrade to Pro for 300 credits/month."}
                </Text>
              </Box>
              <Button size="sm" colorScheme={isExhaustedCredits || isLowCredits ? "red" : "brand"} onClick={() => openTopUp('topup')}>Top Up</Button>
            </Alert>
          )}

          {/* ── AI Credits ──────────────────────────────────────── */}
          <Box>
            <Flex justify="space-between" align="baseline" mb={4}>
              <Text fontSize="md" fontWeight="700" color="gray.900">AI Credits</Text>
              <Badge px={2} py={0.5} borderRadius="md" fontSize="xs"
                colorScheme={isExhaustedCredits || isLowCredits ? "red" : isWarningCredits ? "orange" : "gray"}
                variant="subtle">
                {creditTotal} remaining
              </Badge>
            </Flex>

            <VStack spacing={4} align="stretch">
              {/* Monthly allowance row */}
              <Box>
                <Flex align="flex-start" gap={4}>
                  <Box minW="160px">
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">Monthly allowance</Text>
                    <Text fontSize="xs" color="gray.400" mt={0.5}>Resets {formatResetDate(credits.resetAt)}</Text>
                  </Box>
                  <Box flex={1}>
                    <Progress
                      value={creditAllowance > 0 ? Math.min(100, (creditsSpent / creditAllowance) * 100) : 0}
                      size="sm" borderRadius="full" mt={1}
                      colorScheme={isExhaustedCredits || isLowCredits ? "red" : isWarningCredits ? "red" : "brand"}
                      bg="gray.100"
                    />
                  </Box>
                  <Text fontSize="sm" color={isExhaustedCredits || isLowCredits ? "red.500" : "gray.600"} minW="80px" textAlign="right">
                    {creditsSpent} / {creditAllowance} used
                  </Text>
                </Flex>
              </Box>

              {/* Top-up credits row — only if they have any */}
              {purchasedBalance > 0 && (
                <Box>
                  <Flex align="flex-start" gap={4}>
                    <Box minW="160px">
                      <Text fontSize="sm" fontWeight="medium" color="gray.800">Top-up credits</Text>
                      <Text fontSize="xs" color="gray.400" mt={0.5}>Never expire</Text>
                    </Box>
                    <Box flex={1} />
                    <Text fontSize="sm" color="gray.600" minW="80px" textAlign="right">
                      {purchasedBalance} available
                    </Text>
                  </Flex>
                </Box>
              )}
            </VStack>

            {/* Spending breakdown — stacked bar */}
            {totalSpent > 0 && (
              <Box mt={5} pt={5} borderTop="1px" borderColor="gray.100">
                <Flex justify="space-between" align="baseline" mb={3}>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Spent this period
                  </Text>
                  <Text fontSize="xs" color="gray.400">{totalSpent} cr total</Text>
                </Flex>
                <StackedCreditBar
                  segments={spendSegments}
                  total={creditAllowance}
                  remaining={creditTotal}
                  height="10px"
                />
                <Flex wrap="wrap" gap={3} mt={2.5}>
                  {spendSegments.map(({ key, value }) => (
                    <HStack key={key} spacing={1.5}>
                      <Box w="8px" h="8px" borderRadius="sm" bg={FEATURE_COLORS[key] || FEATURE_COLORS.other} flexShrink={0} />
                      <Text fontSize="xs" color="gray.500">{ACTION_LABELS[key] || key}</Text>
                      <Text fontSize="xs" color="gray.400">{value} cr</Text>
                    </HStack>
                  ))}
                  <HStack spacing={1.5}>
                    <Box w="8px" h="8px" borderRadius="sm" bg="gray.200" flexShrink={0} />
                    <Text fontSize="xs" color="gray.400">{creditTotal} remaining</Text>
                  </HStack>
                </Flex>
              </Box>
            )}

            {/* Top up CTA — right-aligned, inline */}
            <Flex justify="flex-end" mt={4}>
              <Button
                size="sm" bg="gray.900" color="white" borderRadius="lg"
                _hover={{ bg: "gray.700" }} fontWeight="600" px={5}
                onClick={() => openTopUp('topup')}
              >
                Top up credits
              </Button>
            </Flex>
          </Box>

          <Divider />

          {/* ── Account ─────────────────────────────────────────── */}
          <Box>
            <Text fontSize="md" fontWeight="700" color="gray.900" mb={4}>Account</Text>

            <VStack spacing={4} align="stretch">
              {/* Projects row */}
              <Box>
                <Flex align="flex-start" gap={4}>
                  <Box minW="160px">
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">Projects</Text>
                    <Text fontSize="xs" color="gray.400" mt={0.5}>
                      {limits.extraProjectSlots > 0
                        ? `10 base + ${limits.extraProjectSlots} extra`
                        : 'Active workspaces'}
                    </Text>
                  </Box>
                  <Box flex={1}>
                    <Progress
                      value={limits.maxPlans > 0 ? Math.min(100, (plansUsed / limits.maxPlans) * 100) : 0}
                      size="sm" borderRadius="full" mt={1}
                      colorScheme={plansUsed >= limits.maxPlans ? "red" : "brand"}
                      bg="gray.100"
                    />
                  </Box>
                  <Text fontSize="sm" color={plansUsed >= limits.maxPlans ? "red.500" : "gray.600"} minW="80px" textAlign="right">
                    {plansUsed} / {limits.maxPlans} used
                  </Text>
                </Flex>
                {plansUsed >= limits.maxPlans && (
                  <Flex justify="flex-end" mt={2}>
                    <Button
                      size="xs" variant="outline" borderRadius="lg"
                      borderColor="gray.300" color="gray.600"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => openTopUp(isPro ? 'project' : 'upgrade')}
                    >
                      + Add project
                    </Button>
                  </Flex>
                )}
              </Box>

              {/* AI chats row */}
              {chatsMax !== null && (
                <Box>
                  <Flex align="flex-start" gap={4}>
                    <Box minW="160px">
                      <Text fontSize="sm" fontWeight="medium" color="gray.800">AI chats today</Text>
                      <Text fontSize="xs" color="gray.400" mt={0.5}>Resets at midnight</Text>
                    </Box>
                    {isPro ? (
                      <>
                        <Box flex={1} />
                        <Text fontSize="sm" color="gray.500" minW="80px" textAlign="right">Unlimited</Text>
                      </>
                    ) : (
                      <>
                        <Box flex={1}>
                          <Progress
                            value={chatsMax > 0 ? Math.min(100, (chatsUsed / chatsMax) * 100) : 0}
                            size="sm" borderRadius="full" mt={1}
                            colorScheme={chatsUsed >= chatsMax ? "red" : isNearChatLimit ? "red" : "brand"}
                            bg="gray.100"
                          />
                        </Box>
                        <Text fontSize="sm" color={chatsUsed >= chatsMax ? "red.500" : "gray.600"} minW="80px" textAlign="right">
                          {chatsUsed} / {chatsMax} used
                        </Text>
                      </>
                    )}
                  </Flex>
                </Box>
              )}

              {/* AI chats — already rendered above, skip re-adding */}

              {/* Feature limits */}
              {[
                { label: 'Strategy regenerations', sub: 'Per project', limitKey: 'maxStrategyRegens' },
                { label: 'Competitor analysis', sub: 'Per project', limitKey: 'maxCompetitorRegens' },
                { label: 'Product documents', sub: 'Per project', limitKey: 'maxDocsGenerations' },
                { label: 'Action plans', sub: 'Per project', limitKey: 'maxActionPlanGenerations' },
                { label: 'Jobs & missions', sub: 'Per project', limitKey: 'maxAgentTasks' },
                { label: 'Pulse check-ins', sub: 'Strategic check-ins', limitKey: 'maxPulseGenerations' },
                { label: 'Learnings', sub: 'Captured insights', limitKey: 'maxLearningsEvaluations' },
                { label: 'MCP tool calls', sub: 'AI builder connections', limitKey: null },
              ].map(({ label, sub, limitKey }) => {
                const limit = limitKey ? limits[limitKey] : null;
                const isUnlimited = !limitKey || limit === null || limit === undefined;
                return (
                  <Box key={label}>
                    <Flex align="flex-start" gap={4}>
                      <Box minW="160px">
                        <Text fontSize="sm" fontWeight="medium" color="gray.800">{label}</Text>
                        <Text fontSize="xs" color="gray.400" mt={0.5}>{sub}</Text>
                      </Box>
                      <Box flex={1} />
                      <Text fontSize="sm" color="gray.500" minW="80px" textAlign="right">
                        {isUnlimited ? 'Unlimited' : `${limit} per project`}
                      </Text>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          </Box>

          <Divider />

          {/* ── Activity by Project ─────────────────────────────── */}
          {plansList.length > 0 && (
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="gray.900" mb={1}>Activity by Project</Text>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Where your credits went, broken down by project.
              </Text>

              <VStack spacing={3} align="stretch">
                {plansList.map((plan) => {
                  const stratCount    = plan.paidStrategyCount ?? plan.strategyCount ?? 0;
                  const compCount     = plan.competitorCount || 0;
                  const docsCount     = plan.docsCount       || 0;
                  const actionCount   = plan.actionPlanCount || 0;
                  const pulseCount    = plan.pulseCount      || 0;
                  const agentTaskCount = plan.agentTaskCount || 0;
                  const mcpCallCount   = plan.mcpCallCount   || 0;

                  const planFeatures = [
                    { key: 'strategy-section',    count: stratCount,     cr: stratCount     * CREDIT_COSTS['strategy-section'] },
                    { key: 'competitor-analysis', count: compCount,      cr: compCount      * CREDIT_COSTS['competitor-analysis'] },
                    { key: 'document',            count: docsCount,      cr: docsCount      * CREDIT_COSTS['document'] },
                    { key: 'action-plan',         count: actionCount,    cr: actionCount    * CREDIT_COSTS['action-plan'] },
                    { key: 'pulse',               count: pulseCount,     cr: pulseCount     * CREDIT_COSTS['pulse'] },
                    { key: 'agent-task',          count: agentTaskCount, cr: agentTaskCount * CREDIT_COSTS['agent-task'] },
                    { key: 'mcp-call',            count: mcpCallCount,   cr: mcpCallCount   * CREDIT_COSTS['mcp-call'] },
                  ].filter(f => f.count > 0);

                  const planCredits = planFeatures.reduce((s, f) => s + f.cr, 0);
                  const hasActivity = planCredits > 0;

                  return (
                    <Box key={plan.id} bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" p={5}>
                      <Flex justify="space-between" align="center" mb={hasActivity ? 4 : 0}>
                        <Text fontWeight="semibold" color="gray.800" fontSize="sm">{plan.name}</Text>
                        {hasActivity && (
                          <Text fontSize="xs" color="gray.500" fontWeight="medium">{planCredits} cr used</Text>
                        )}
                      </Flex>

                      {hasActivity ? (
                        <>
                          {/* Mini stacked bar for this project */}
                          <StackedCreditBar
                            segments={planFeatures.map(f => ({ key: f.key, value: f.cr }))}
                            total={planCredits}
                            showRemaining={false}
                            height="8px"
                          />
                          {/* Feature rows — runs first, credits second */}
                          <VStack spacing={1.5} align="stretch" mt={3}>
                            {planFeatures.map(f => (
                              <Flex key={f.key} justify="space-between" align="center">
                                <HStack spacing={1.5}>
                                  <Box w="8px" h="8px" borderRadius="sm" bg={FEATURE_COLORS[f.key]} flexShrink={0} />
                                  <Text fontSize="xs" color="gray.600">{ACTION_LABELS[f.key]}</Text>
                                </HStack>
                                <Text fontSize="xs" color="gray.500">
                                  {f.count} run{f.count !== 1 ? 's' : ''} · {f.cr} cr
                                </Text>
                              </Flex>
                            ))}
                          </VStack>
                        </>
                      ) : (
                        <Text fontSize="xs" color="gray.400" mt={2}>No AI activity yet</Text>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}

        </VStack>
      </Box>

      <CreditTopUpModal
        isOpen={isTopUpOpen}
        onClose={() => { setIsTopUpOpen(false); fetchStatus(); }}
        hasSavedPaymentMethod={planStatus?.hasSavedPaymentMethod}
        planType={planStatus?.planType}
        initialMode={topUpInitialMode}
        resetAt={planStatus?.credits?.resetAt}
      />
    </UserSettingsLayout>
  );
};

export default PlanUsage;
